import React, { useEffect, useRef, useState } from 'react';
import { FilesetResolver, FaceLandmarker } from '@mediapipe/tasks-vision';
import { Camera } from '@mediapipe/camera_utils';
import './FaceCaptureComponent.css';

const FaceCaptureComponent = ({ apiEndpoint, goBack, isTest, action }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const faceLandmarkerRef = useRef(null);
    const cameraRef = useRef(null);
    const streamRef = useRef(null); // Ref to hold the stream for cleanup

    const isCapturing = useRef(true);
    const isSendingRef = useRef(false);
    const lastFaceDetectionTimeRef = useRef(Date.now());
    const inactivityIntervalRef = useRef(null);
    const modalAutoCloseTimerRef = useRef(null);

    // Refs for loop access to avoid dependency issues
    const blinkDetectedRef = useRef(false);
    const spoofDetectedRef = useRef(false);

    const [responseData, setResponseData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [userId, setUserId] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    const dressCode = 0; // Default to 0 (no dress code) as per backend requirement
    const [countdown, setCountdown] = useState(60);
    const [modalAutoCloseCountdown, setModalAutoCloseCountdown] = useState(5);
    const [detectorReady, setDetectorReady] = useState(false);
    const [qualityMessage, setQualityMessage] = useState('');
    const stableFrameCountRef = useRef(0);
    const lastCaptureTimeRef = useRef(0);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent || ''
    );
    const previousBboxRef = useRef(null);
    const canvasContextRef = useRef(null);
    const lastCanvasSizeRef = useRef({ width: 0, height: 0 });

    // Blink detection state
    const eyeStateHistory = useRef({ closed: 0, openAfterClose: 0 });
    const BLINK_THRESHOLD = 0.25; // EAR threshold for closed eye
    // Relaxed thresholds for mobile
    const TEXTURE_MIN_VARIANCE = 1.0;
    const TEXTURE_MAX_VARIANCE = 6000;
    const BLUR_THRESHOLD = 20; // Require a sharper frame before capture
    const STABLE_FRAMES_DESKTOP = 12;
    const STABLE_FRAMES_MOBILE = 14;
    const MOTION_TOLERANCE = 0.025; // How much movement between frames is allowed

    // Initialize face landmarker
    useEffect(() => {
        let isMounted = true;
        console.log("Initializing FaceLandmarker...");

        const initLandmarker = async () => {
            try {
                const vision = await FilesetResolver.forVisionTasks(
                    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm'
                );
                if (isMounted) {
                    faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
                        baseOptions: {
                            modelAssetPath:
                                'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
                            delegate: "GPU"
                        },
                        runningMode: 'VIDEO',
                        numFaces: 5,
                        minFaceDetectionConfidence: 0.3,
                        minFacePresenceConfidence: 0.3,
                        minTrackingConfidence: 0.3,
                        outputFaceBlendshapes: true,
                    });
                    console.log("FaceLandmarker initialized successfully.");
                    setDetectorReady(true);
                }
            } catch (error) {
                console.error("Failed to initialize FaceLandmarker:", error);
            }
        };

        initLandmarker();

        return () => {
            isMounted = false;
            stopCamera();
            clearInactivityTimer();
            clearModalAutoCloseTimer();
            if (faceLandmarkerRef.current) {
                faceLandmarkerRef.current.close();
                faceLandmarkerRef.current = null;
            }
        };
    }, []);

    // Auto-start camera for test mode
    useEffect(() => {
        if (isTest && detectorReady && !cameraActive) {
            console.log("Auto-starting camera for test mode...");
            startCamera();
        }
    }, [isTest, detectorReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // Inactivity timer
    useEffect(() => {
        if (cameraActive && !showModal) {
            inactivityIntervalRef.current = setInterval(() => {
                const timeSinceLastFace = Date.now() - lastFaceDetectionTimeRef.current;
                const remainingSeconds = Math.max(0, 60 - Math.floor(timeSinceLastFace / 1000));
                setCountdown(remainingSeconds);

                if (timeSinceLastFace >= 60000) {
                    clearInactivityTimer();
                    stopCamera();
                    goBack();
                }
            }, 1000);
        } else {
            clearInactivityTimer();
        }
        return () => clearInactivityTimer();
    }, [cameraActive, showModal, goBack]);

    // Handle visibility changes (background/minimize)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log("App moved to background - verifying camera cleanup");
                if (cameraActive) {
                    stopCamera();
                }
            } else {
                console.log("App returned to foreground");
                // We keep the camera stopped to save resources
                // User can manually restart if needed
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [cameraActive]); // Depend on cameraActive to know if we need to stop

    const clearInactivityTimer = () => {
        if (inactivityIntervalRef.current) {
            clearInterval(inactivityIntervalRef.current);
            inactivityIntervalRef.current = null;
        }
    };

    const clearModalAutoCloseTimer = () => {
        if (modalAutoCloseTimerRef.current) {
            clearInterval(modalAutoCloseTimerRef.current);
            modalAutoCloseTimerRef.current = null;
        }
    };

    const resetInactivityTimer = () => {
        lastFaceDetectionTimeRef.current = Date.now();
        setCountdown(60);
    };

    // --- Anti-Spoofing & Quality Logic ---

    const calculateEAR = (landmarks) => {
        const leftEye = [33, 160, 158, 133, 153, 144];
        const rightEye = [362, 385, 387, 263, 373, 380];
        const getDist = (p1, p2) => Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
        const getEAR = (indices) => {
            const p = indices.map(i => landmarks[i]);
            const v1 = getDist(p[1], p[5]);
            const v2 = getDist(p[2], p[4]);
            const h = getDist(p[0], p[3]);
            return (v1 + v2) / (2.0 * h);
        };
        return (getEAR(leftEye) + getEAR(rightEye)) / 2;
    };

    const detectBlink = (ear) => {
        const state = eyeStateHistory.current;
        const isClosed = ear < BLINK_THRESHOLD;

        if (isClosed) {
            state.closed = Math.min(state.closed + 1, 5);
            state.openAfterClose = 0;
            return false;
        }

        if (state.closed >= 2) {
            state.openAfterClose += 1;
            if (state.openAfterClose >= 2 && ear > BLINK_THRESHOLD + 0.05) {
                state.closed = 0;
                state.openAfterClose = 0;
                return true;
            }
        } else {
            state.closed = 0;
            state.openAfterClose = 0;
        }
        return false;
    };

    const calculateBlurScore = (ctx, bbox) => {
        const { x, y, width, height } = bbox;
        if (width <= 0 || height <= 0) return 0;
        const imageData = ctx.getImageData(x, y, width, height);
        const pixels = imageData.data;
        const w = Math.floor(width);
        const h = Math.floor(height);
        let sumVar = 0;
        let count = 0;
        const gray = new Uint8Array(w * h);

        for (let i = 0, j = 0; i < pixels.length; i += 4, j++) {
            gray[j] = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
        }

        for (let r = 1; r < h - 1; r += 2) {
            for (let c = 1; c < w - 1; c += 2) {
                const idx = r * w + c;
                const laplacian = Math.abs(4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w]);
                sumVar += laplacian * laplacian;
                count++;
            }
        }
        return count > 0 ? sumVar / count : 0;
    };

    const checkTextureVariance = (ctx, bbox) => {
        const { x, y, width, height } = bbox;
        if (width <= 0 || height <= 0) return { variance: 0, stdDev: 0, isReal: false };

        const imageData = ctx.getImageData(x, y, width, height);
        const pixels = imageData.data;
        const values = [];

        const sampleStep = 20;
        for (let i = 0; i < pixels.length; i += sampleStep * 4) {
            const gray = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
            values.push(gray);
        }

        if (values.length === 0) return { variance: 0, stdDev: 0, isReal: false };

        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);

        const isReal = variance >= TEXTURE_MIN_VARIANCE && variance <= TEXTURE_MAX_VARIANCE && stdDev >= 1.0;

        return { variance, stdDev, isReal };
    };

    const isFaceStable = (bbox, prevBbox, canvas) => {
        if (!prevBbox) return true;
        const centerX = bbox.x + bbox.width / 2;
        const centerY = bbox.y + bbox.height / 2;
        const prevCenterX = prevBbox.x + prevBbox.width / 2;
        const prevCenterY = prevBbox.y + prevBbox.height / 2;

        const deltaX = Math.abs(centerX - prevCenterX) / canvas.width;
        const deltaY = Math.abs(centerY - prevCenterY) / canvas.height;
        const widthChange = Math.abs(bbox.width - prevBbox.width) / Math.max(bbox.width, 1);
        const heightChange = Math.abs(bbox.height - prevBbox.height) / Math.max(bbox.height, 1);

        const movementScore = (deltaX + deltaY) / 2;
        const sizeScore = (widthChange + heightChange) / 2;

        return movementScore < MOTION_TOLERANCE && sizeScore < MOTION_TOLERANCE * 1.5;
    };

    const startCamera = async () => {
        if (!faceLandmarkerRef.current) {
            console.warn("FaceLandmarker not ready yet.");
            return;
        }
        if (cameraRef.current) await stopCamera();

        console.log("Starting camera...");
        isCapturing.current = true;
        isSendingRef.current = false;
        resetInactivityTimer();

        blinkDetectedRef.current = false;
        spoofDetectedRef.current = false;
        eyeStateHistory.current = { closed: 0, openAfterClose: 0 };
        setCameraActive(true);
    };

    const stopCamera = async () => {
        console.log("Stopping camera...");
        isCapturing.current = false;

        // Synchronous Hardware Stop - Immediate Effect
        if (streamRef.current) {
            const tracks = streamRef.current.getTracks();
            tracks.forEach(track => {
                track.stop();
                console.log("Stream track stopped (via streamRef):", track.kind);
            });
            streamRef.current = null;
        }

        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach(track => {
                track.stop();
                console.log("Camera track stopped (via videoRef):", track.kind);
            });
            videoRef.current.srcObject = null;
        }

        setCameraActive(false);

        // Async Engine Cleanup
        if (cameraRef.current) {
            await cameraRef.current.stop();
            cameraRef.current = null;
        }
    };

    const pauseCamera = () => {
        isCapturing.current = false;
    };

    const resumeCamera = () => {
        console.log("Resuming camera...");
        isCapturing.current = true;
        resetInactivityTimer();
        blinkDetectedRef.current = false;
        spoofDetectedRef.current = false;
        eyeStateHistory.current = { closed: 0, openAfterClose: 0 };
    };

    // --- Main Loop ---
    useEffect(() => {
        let isEffectActive = true;

        if (cameraActive && videoRef.current && !showModal) {
            console.log("Initializing Camera loop...");

            // First, manually request camera access with explicit constraints
            const initCamera = async () => {
                try {
                    // Define camera constraints
                    const constraints = {
                        video: {
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                            facingMode: 'user' // Request front-facing camera
                        },
                        audio: false
                    };

                    console.log("Requesting camera access with constraints:", constraints);

                    // Request camera access
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);

                    if (!isEffectActive) {
                        console.log("Camera effect cleaned up before stream acquire. Stopping zombie stream.");
                        stream.getTracks().forEach(track => track.stop());
                        return;
                    }

                    console.log("Camera access granted, stream obtained:", stream);

                    // Store the stream reference
                    streamRef.current = stream;

                    // Assign stream to video element
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                    }

                    // Now initialize MediaPipe Camera
                    const cam = new Camera(videoRef.current, {
                        onFrame: async () => {
                            if (!faceLandmarkerRef.current || !isCapturing.current || isSendingRef.current || showModal) return;

                            const canvas = canvasRef.current;

                            // Optimization: Cache context and use willReadFrequently
                            let ctx = canvasContextRef.current;
                            if (!ctx) {
                                ctx = canvas.getContext('2d', { willReadFrequently: true });
                                canvasContextRef.current = ctx;
                            }

                            const vWidth = videoRef.current.videoWidth;
                            const vHeight = videoRef.current.videoHeight;

                            // Optimization: Only resize canvas if dimensions change to prevent memory leaks
                            if (lastCanvasSizeRef.current.width !== vWidth || lastCanvasSizeRef.current.height !== vHeight) {
                                canvas.width = vWidth;
                                canvas.height = vHeight;
                                lastCanvasSizeRef.current = { width: vWidth, height: vHeight };
                            } else {
                                // Explicitly clear common area if not resizing (resize clears automatically)
                                ctx.clearRect(0, 0, canvas.width, canvas.height);
                            }
                            const zoomFactor = isMobile ? 1.0 : 0.5;
                            const cropWidth = vWidth / zoomFactor;
                            const cropHeight = vHeight / zoomFactor;
                            const cropX = (vWidth - cropWidth) / 2;
                            const cropY = (vHeight - cropHeight) / 2;

                            ctx.drawImage(videoRef.current, cropX, cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);

                            const results = await faceLandmarkerRef.current.detectForVideo(videoRef.current, performance.now());

                            if (results.faceLandmarks && results.faceLandmarks.length > 0) {
                                console.log(`Detected ${results.faceLandmarks.length} faces`);
                                // Iterate over all faces
                                for (let i = 0; i < results.faceLandmarks.length; i++) {
                                    const landmarks = results.faceLandmarks[i];
                                    const isPrimary = (i === 0); // Only perform capture/validation logic on primary face

                                    const xs = landmarks.map(l => (zoomFactor * l.x - ((zoomFactor - 1) / 2)) * canvas.width);
                                    const ys = landmarks.map(l => (zoomFactor * l.y - ((zoomFactor - 1) / 2)) * canvas.height);

                                    const minX = Math.min(...xs);
                                    const maxX = Math.max(...xs);
                                    const minY = Math.min(...ys);
                                    const maxY = Math.max(...ys);
                                    const bbox = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };

                                    // Default drawing style for secondary faces
                                    let message = '';
                                    let boxColor = '#007bff'; // Blue for detected but not processing
                                    let canSend = false;

                                    if (isPrimary) {
                                        const stableFace = isFaceStable(bbox, previousBboxRef.current, canvas);
                                        const textureCheck = checkTextureVariance(ctx, bbox);
                                        const ear = calculateEAR(landmarks);
                                        const isBlinking = detectBlink(ear);

                                        if (isBlinking) {
                                            blinkDetectedRef.current = true;
                                        }

                                        const blurScore = calculateBlurScore(ctx, bbox);
                                        const isSharp = blurScore > BLUR_THRESHOLD;

                                        const isVisible = minX >= 0 && minY >= 0 && maxX <= canvas.width && maxY <= canvas.height;
                                        const isCentered = (bbox.x > 10 && bbox.y > 10 && (bbox.x + bbox.width) < (canvas.width - 10) && (bbox.y + bbox.height) < (canvas.height - 10));
                                        const isCloseEnough = bbox.width > (canvas.width * 0.15);

                                        const noseTip = landmarks[1];
                                        const leftEye = landmarks[33];
                                        const rightEye = landmarks[263];

                                        // Strict Frontal Check
                                        const dLeft = Math.abs(noseTip.x - leftEye.x);
                                        const dRight = Math.abs(noseTip.x - rightEye.x);
                                        const symmetryRatio = (dLeft > 0 && dRight > 0) ?
                                            Math.min(dLeft, dRight) / Math.max(dLeft, dRight) : 0;
                                        const isFrontal = symmetryRatio > 0.50;

                                        // if (!textureCheck.isReal && !spoofDetectedRef.current && isVisible) {
                                        //     console.warn(`🚨 SPOOF DETECTED! Variance: ${textureCheck.variance.toFixed(0)}, StdDev: ${textureCheck.stdDev.toFixed(1)}`);
                                        //     pauseCamera();
                                        //     spoofDetectedRef.current = true;
                                        //     setQualityMessage('Spoof detected - remove screen/photo');
                                        //     setResponseData({
                                        //         type: 'error',
                                        //         message: 'Spoof Detected!',
                                        //         title: 'Security Alert',
                                        //         details: 'Artificial display or photo detected. Please use a live face.'
                                        //     });
                                        //     setShowModal(true);
                                        //     return;
                                        // }

                                        if (spoofDetectedRef.current) return;

                                        if (!isVisible) {
                                            message = 'Move into frame';
                                            boxColor = '#ffc107';
                                        } else if (!isCentered) {
                                            message = 'Center your face';
                                            boxColor = '#007bff';
                                        } else if (!isCloseEnough) {
                                            message = 'Move closer';
                                            boxColor = '#ffc107';
                                        } else if (!isFrontal) {
                                            message = 'Face the camera straight';
                                            boxColor = '#ff9800';
                                        } else if (!isSharp) {
                                            message = 'Hold still - focus';
                                            boxColor = '#dc3545';
                                        } else if (!stableFace) {
                                            message = 'Hold still - stabilizing';
                                            boxColor = '#dc3545';
                                        } else {
                                            // Require a blink before capture for desktop only
                                            if (!isMobile && !blinkDetectedRef.current) {
                                                message = 'Blink to Verify';
                                                boxColor = '#17a2b8';
                                            } else {
                                                message = 'Perfect - Capturing...';
                                                boxColor = '#28a745';
                                                canSend = true;
                                            }
                                        }
                                        setQualityMessage(message);
                                        previousBboxRef.current = bbox;
                                    }

                                    // Draw Box
                                    ctx.lineWidth = 4;
                                    ctx.strokeStyle = boxColor;
                                    ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);

                                    // Draw Message if present (Primary only)
                                    if (isPrimary && message) {
                                        ctx.save();
                                        ctx.scale(-1, 1);
                                        ctx.font = 'bold 18px "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
                                        const textWidth = ctx.measureText(message).width;
                                        const textX = -(bbox.x + (bbox.width / 2) + (textWidth / 2));
                                        const textY = bbox.y - 15;
                                        const padding = 8;

                                        ctx.fillStyle = boxColor;
                                        ctx.beginPath();
                                        ctx.roundRect(textX - padding, textY - 20, textWidth + (padding * 2), 30, 6);
                                        ctx.fill();

                                        ctx.fillStyle = '#ffffff';
                                        ctx.textAlign = 'left';
                                        ctx.textBaseline = 'middle';
                                        ctx.fillText(message, textX, textY - 5);
                                        ctx.restore();
                                    }

                                    // Capture Logic (Primary only)
                                    if (isPrimary) {
                                        if (canSend) {
                                            stableFrameCountRef.current += 1;
                                            const stableNeeded = isMobile ? STABLE_FRAMES_MOBILE : STABLE_FRAMES_DESKTOP;
                                            const readyToSend =
                                                (isMobile && stableFrameCountRef.current >= stableNeeded) ||
                                                (!isMobile && blinkDetectedRef.current && stableFrameCountRef.current >= stableNeeded);

                                            const minIntervalPassed = Date.now() - lastCaptureTimeRef.current > 1200;

                                            if (readyToSend && minIntervalPassed) {
                                                resetInactivityTimer();
                                                lastCaptureTimeRef.current = Date.now();
                                                await processAndSendFrame(canvas, results.faceLandmarks, zoomFactor);
                                                stableFrameCountRef.current = 0;
                                            } else {
                                                resetInactivityTimer();
                                            }
                                        } else {
                                            stableFrameCountRef.current = 0;
                                            resetInactivityTimer();
                                        }
                                    }
                                }

                            } else {
                                setQualityMessage('No face detected');
                                previousBboxRef.current = null;
                            }
                        },
                        width: 1280,
                        height: 720,
                    });

                    cameraRef.current = cam;
                    if (isEffectActive) {
                        await cam.start();
                    } else {
                        console.log("Effect inactive after camera init, stopping camera.");
                        await cam.stop();
                    }

                } catch (error) {
                    if (!isEffectActive) return;

                    console.error("Failed to initialize camera:", error);

                    let errorMessage = 'Failed to access camera';
                    let errorDetails = '';

                    if (error.name === 'NotFoundError') {
                        errorMessage = 'Camera Not Found';
                        errorDetails = 'No camera device was found on your device. Please ensure a camera is connected and try again.';
                    } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                        errorMessage = 'Camera Permission Denied';
                        errorDetails = 'Please allow camera access in your browser settings and refresh the page.';
                    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                        errorMessage = 'Camera Already in Use';
                        errorDetails = 'The camera is already being used by another application. Please close other apps using the camera and try again.';
                    } else if (error.name === 'OverconstrainedError') {
                        errorMessage = 'Camera Constraints Not Supported';
                        errorDetails = 'Your camera does not support the required settings. Please try a different device.';
                    } else {
                        errorDetails = error.message || 'An unknown error occurred while trying to access the camera.';
                    }

                    setResponseData({
                        type: 'error',
                        message: errorMessage,
                        title: 'Camera Error',
                        details: errorDetails
                    });
                    setShowModal(true);
                    setCameraActive(false);
                }
            };

            initCamera();

            return () => {
                isEffectActive = false;
                console.log("Cleaning up Camera loop...");
                if (cameraRef.current) {
                    cameraRef.current.stop();
                    cameraRef.current = null;
                }
                if (streamRef.current) {
                    const tracks = streamRef.current.getTracks();
                    tracks.forEach(track => {
                        track.stop();
                        console.log("Cleanup: stopped stream track", track.kind);
                    });
                    streamRef.current = null;
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                }
            };
        }
    }, [cameraActive, showModal, isMobile]);

    const processAndSendFrame = async (canvas, landmarksList = null, zoomFactor = 1.0) => {
        if (isSendingRef.current) return;
        isSendingRef.current = true;
        pauseCamera();

        const formData = new FormData();

        if (landmarksList && landmarksList.length > 1) {
            console.log(`Processing ${landmarksList.length} faces for upload...`);
            const facePromises = landmarksList.map(async (landmarks, index) => {
                // Re-calculate bbox for cropping (same logic as onFrame)
                const xs = landmarks.map(l => (zoomFactor * l.x - ((zoomFactor - 1) / 2)) * canvas.width);
                const ys = landmarks.map(l => (zoomFactor * l.y - ((zoomFactor - 1) / 2)) * canvas.height);
                let minX = Math.min(...xs);
                let maxX = Math.max(...xs);
                let minY = Math.min(...ys);
                let maxY = Math.max(...ys);

                // Add some padding (e.g., 20% of face width/height)
                const padW = (maxX - minX) * 0.2;
                const padH = (maxY - minY) * 0.2;
                minX = Math.max(0, minX - padW);
                minY = Math.max(0, minY - padH);
                maxX = Math.min(canvas.width, maxX + padW);
                maxY = Math.min(canvas.height, maxY + padH);

                const width = maxX - minX;
                const height = maxY - minY;

                if (width <= 0 || height <= 0) return null;

                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = width;
                tempCanvas.height = height;
                const ctx = tempCanvas.getContext('2d');
                ctx.drawImage(canvas, minX, minY, width, height, 0, 0, width, height);

                return new Promise(resolve => tempCanvas.toBlob(b => resolve({ blob: b, index }), 'image/png'));
            });

            const blobs = (await Promise.all(facePromises)).filter(r => r !== null);
            blobs.forEach(({ blob, index }) => {
                formData.append('frames[]', blob, `frame_${index + 1}.png`);
            });
            console.log(`📤 Sending ${blobs.length} face crops to API.`);

        } else {
            // Single face or fallback - send full frame
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            formData.append('frames[]', blob, 'frame_1.png');
            console.log(`📤 Sending 1 face (full frame) to API.`);
        }

        if (!isTest) {
            formData.append('id', userId);
        } else {
            formData.append('action', action);
            formData.append('dress_code', dressCode);
        }

        console.log(`Sending request to: ${apiEndpoint}`);
        console.log('FormData entries:');
        for (let pair of formData.entries()) {
            console.log(pair[0] + ': ' + (pair[1] instanceof Blob ? `Blob (${pair[1].type}, ${pair[1].size} bytes)` : pair[1]));
        }

        try {
            setSubmitting(true);
            const res = await fetch(apiEndpoint, {
                method: 'POST',
                body: formData,
                mode: 'cors', // Explicitly request CORS
            });

            console.log(`Response status: ${res.status} ${res.statusText}`);

            if (!res.ok) {
                const errorText = await res.text();
                console.error(`Server returned error: ${errorText}`);
                throw new Error(`Server error: ${res.status} ${res.statusText}`);
            }

            const result = await res.json();
            console.log('Response data:', result);
            setResponseData(result);
            setShowModal(true);
            setSubmitting(false);
            isSendingRef.current = false;

            const timerDuration = Array.isArray(result) ? Math.max(5, result.length * 3) : 5;
            startModalAutoCloseTimer(timerDuration);
        } catch (error) {
            console.error('Fetch error details:', error);
            console.error(`Error message: ${error.message}`);

            let errorData = {
                type: 'error',
                title: 'Error',
                message: error.message,
                details: 'An unexpected error occurred.'
            };

            // If it's a TypeError and the network tab shows failed, it's likely CORS or Network Error
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                console.error('Possible CORS issue or Network Connection failure.');
                errorData = {
                    type: 'error',
                    title: 'Network Error',
                    message: 'Failed to connect to the server. This is likely a CORS issue or the server is down.',
                    details: 'Please ensure the backend has CORS enabled and is accessible from this origin.'
                };
            }

            setResponseData(errorData);
            setShowModal(true);
            isSendingRef.current = false;
            setSubmitting(false);

            // Note: We don't call resumeCamera() here because the modal is shown.
            // handleCloseModal will handle resuming or going back.
        }
    };

    const startModalAutoCloseTimer = (duration = 5) => {
        setModalAutoCloseCountdown(duration);
        modalAutoCloseTimerRef.current = setInterval(() => {
            setModalAutoCloseCountdown(prev => {
                if (prev <= 1) {
                    handleCloseModal();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleCloseModal = () => {
        clearModalAutoCloseTimer();
        setShowModal(false);
        setResponseData(null);
        if (isTest) {
            resumeCamera();
        } else {
            stopCamera();
            goBack();
        }
    };

    const actionLabels = {
        punch_in: "Punch In",
        punch_out: "Punch Out",
        break_in: "Break In",
        break_out: "Break Out",
        register: "Register Face"
    };
    const actionLabel = actionLabels[action] || action;
    const modalTitle = isTest ? actionLabel : (responseData?.title || responseData?.message);

    return (
        <div className="face-capture-container">
            <button
                type="button"
                className="floating-back-button"
                onClick={() => { stopCamera(); goBack(); }}
            >
                Back
            </button>
            <div className="neu-card1">
                <span className="registration-link" style={{ fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }} onClick={() => { stopCamera(); goBack(); }}>← Back</span>
                <h2 style={{ color: '#ffffff' }}>{isTest ? actionLabel : 'Register Face'}</h2>

                {!isTest && (
                    <div className="input-group">
                        <input
                            placeholder="Employee Code"
                            value={userId}
                            onChange={e => setUserId(e.target.value)}
                            style={{ width: '80%', height: '2em', fontSize: '1em', padding: '0.5em 1em', borderRadius: '6px', border: '1px solid #ccc', margin: '0em auto', display: 'block' }}
                        />
                    </div>
                )}
                {(userId === '') && !isTest && <p className="warning-text" style={{ color: '#ffffff' }}>Please enter your employee code for registration.</p>}

                {!isTest && (
                    <div className="controls">
                        <button className="btn-primary mb-3" onClick={startCamera}>Start Camera</button>
                    </div>
                )}

                {cameraActive && (
                    <div className="camera-wrapper">
                        <video ref={videoRef} autoPlay muted playsInline />
                        <canvas ref={canvasRef} />
                        <div className="camera-cta">
                            <span style={{ fontWeight: 700, textDecoration: 'none', color: '#21e099', justifyContent: 'center' }}>
                                {actionLabel}
                            </span>
                        </div>
                        {qualityMessage && (
                            <div style={{
                                position: 'absolute',
                                bottom: '10px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: qualityMessage.includes('Perfect') ? 'rgba(0, 255, 0, 0.8)' :
                                    qualityMessage.includes('Blink') ? 'rgba(0, 100, 255, 0.8)' :
                                        qualityMessage.includes('Spoof') ? 'rgba(255, 0, 0, 0.8)' :
                                            'rgba(255, 165, 0, 0.8)',
                                color: '#ffffff',
                                padding: '10px 20px',
                                borderRadius: '8px',
                                fontSize: '1em',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                minWidth: '200px',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
                            }}>
                                {qualityMessage}
                            </div>
                        )}
                        {countdown < 60 && (
                            <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0, 0, 0, 0.7)', color: countdown <= 10 ? '#ff4444' : '#ffffff', padding: '8px 16px', borderRadius: '8px', fontSize: '1.2em', fontWeight: 'bold' }}>
                                {countdown}s
                            </div>
                        )}
                    </div>
                )}

                {submitting && (
                    <div className="loader-overlay">
                        <div className="loader-spinner"></div>
                        <span>Verifying...</span>
                    </div>
                )}

                {showModal && (
                    <div className="modal-overlay">
                        <div className="modal" style={Array.isArray(responseData) ? {
                            width: '90%',
                            maxWidth: '450px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            background: '#1a1a2e',
                            color: '#ffffff'
                        } : {}}>
                            {Array.isArray(responseData) ? (
                                <>
                                    <h3 style={{ color: '#00ff88', marginBottom: '15px' }}>Detection Results ({responseData.length})</h3>
                                    <div className="results-list" style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        width: '100%',
                                        marginBottom: '15px'
                                    }}>
                                        {responseData.map((res, idx) => (
                                            <div key={idx} style={{
                                                border: `2px solid ${res.type === 'error' ? '#ff4444' : '#00C851'}`,
                                                borderRadius: '10px',
                                                padding: '12px',
                                                background: res.type === 'error' ? 'rgba(255, 68, 68, 0.1)' : 'rgba(0, 200, 81, 0.1)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    alignItems: 'flex-start'
                                                }}>
                                                    {/* Status Icon */}
                                                    {res.image && (
                                                        <div style={{ flexShrink: 0 }}>
                                                            <img
                                                                src={`${apiEndpoint.replace('/test-face', '')}/${res.image}`}
                                                                alt="Status"
                                                                style={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    objectFit: 'contain',
                                                                    background: 'rgba(255, 255, 255, 0.9)',
                                                                    borderRadius: '8px',
                                                                    padding: '5px'
                                                                }}
                                                                onError={(e) => e.target.style.display = 'none'}
                                                            />
                                                        </div>
                                                    )}

                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        {/* Name and Badge */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginBottom: '8px',
                                                            flexWrap: 'wrap',
                                                            gap: '8px'
                                                        }}>
                                                            <strong style={{
                                                                fontSize: '1.1em',
                                                                color: '#ffffff',
                                                                wordBreak: 'break-word'
                                                            }}>{res.name || 'Unknown'}</strong>
                                                            <span style={{
                                                                fontSize: '0.75em',
                                                                background: res.type === 'error' ? '#ff4444' : '#00C851',
                                                                color: 'white',
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                fontWeight: 'bold',
                                                                whiteSpace: 'nowrap'
                                                            }}>{res.title || res.type}</span>
                                                        </div>

                                                        {/* Check Status */}
                                                        {res.check && (
                                                            <div style={{
                                                                fontSize: '0.9em',
                                                                fontWeight: '600',
                                                                color: res.type === 'error' ? '#ff6b6b' : '#51cf66',
                                                                marginBottom: '8px',
                                                                padding: '4px 8px',
                                                                background: 'rgba(0, 0, 0, 0.2)',
                                                                borderRadius: '4px',
                                                                display: 'inline-block'
                                                            }}>
                                                                {res.check}
                                                            </div>
                                                        )}

                                                        {/* Message */}
                                                        <p style={{
                                                            margin: '6px 0',
                                                            fontSize: '0.95em',
                                                            color: '#e0e0e0',
                                                            lineHeight: '1.4'
                                                        }}>{res.message}</p>

                                                        {/* Score and ID */}
                                                        {isTest && (
                                                            <p style={{
                                                                margin: '6px 0 0 0',
                                                                fontSize: '0.8em',
                                                                color: '#999',
                                                                fontFamily: 'monospace'
                                                            }}>
                                                                Score: {res.score ? res.score.toFixed(3) : 'N/A'} | ID: {res.id}
                                                            </p>
                                                        )}

                                                        {/* Face Thumbnail */}
                                                        {res.frame_name && isTest && (
                                                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                                                <img
                                                                    src={`${apiEndpoint.replace('/test-face', '')}/uploads/unknown/${res.frame_name}`}
                                                                    alt="Face"
                                                                    style={{
                                                                        maxHeight: '70px',
                                                                        borderRadius: '6px',
                                                                        border: '2px solid rgba(255, 255, 255, 0.2)',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                    onError={(e) => e.target.style.display = 'none'}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3 style={responseData?.type === "error" ? { color: "red" } : {}}>{modalTitle}</h3>
                                    {responseData?.message && isTest && <p>{responseData.message}</p>}
                                    {responseData?.details && <p>{responseData.details}</p>}

                                    {!responseData?.details && !isTest && (
                                        <>
                                            <p><strong>Name :</strong> {responseData?.employee?.name || responseData?.user?.employee?.name || 'Unknown'}</p>
                                            <p><strong>Employee Code:</strong> {responseData?.employee?.code || responseData?.user?.employee?.code || 'Unknown'}</p>
                                            <p><strong>Mobile:</strong> {responseData?.employee?.mob_no || responseData?.user?.employee?.mob_no || 'Unknown'}</p>
                                        </>
                                    )}
                                    {!responseData?.details && isTest && (
                                        <>
                                            <p><strong>Name :</strong> {responseData?.name || 'Unknown'}</p>
                                            <p><strong>Employee Code:</strong> {responseData?.id || 'Unknown'}</p>
                                        </>
                                    )}
                                </>
                            )}

                            <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
                                Auto-closing in {modalAutoCloseCountdown}s
                            </p>
                            <button className='btn-secondary' onClick={handleCloseModal}>OK</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FaceCaptureComponent;
